import moment from "moment";
import { getCompanyInfo } from "@/api/company";

export default {
  data() {
    return {
      printing: false,
      printGeneratedAt: null,
      printOrientation: "portrait",
      companyPrintLogo: null,
    };
  },

  computed: {
    printEmitter() {
      const profileName = [
        this.profile && this.profile.name,
        this.profile && this.profile.surname,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      return (
        profileName ||
        (this.profile && this.profile.email) ||
        "Usuário não identificado"
      );
    },

    printCompany() {
      if (this.showCompanyFilter) {
        if (!this.filter || !this.filter.selectedCompany) {
          return "Todas Empresas";
        }

        const selectedCompany = (this.companyOptions || []).find(
          (company) => company.value === this.filter.selectedCompany
        );

        return selectedCompany ? selectedCompany.text : "Empresa Selecionada";
      }

      if (this.report && this.report.company) {
        return (
          this.report.company.fantasyName ||
          this.report.company.name ||
          "Empresa não identificada"
        );
      }

      return "Empresa não identificada";
    },

    printLogo() {
      if (this.companyPrintLogo) {
        return this.companyPrintLogo;
      }

      return new URL("img/logo.png", window.location.href).href;
    },

    printPeriod() {
      const filter = this.filter || {};
      const fromDate = filter.fromDate || filter.startDate;
      const toDate = filter.toDate || filter.endDate;

      if (fromDate && toDate) {
        return `${this.formatPrintDate(fromDate)} a ${this.formatPrintDate(
          toDate
        )}`;
      }
      if (fromDate) {
        return `A partir de ${this.formatPrintDate(fromDate)}`;
      }
      if (toDate) {
        return `Até ${this.formatPrintDate(toDate)}`;
      }

      return "Todos os períodos";
    },

    printFields() {
      let fields = [];

      if (Array.isArray(this.activeFields)) {
        fields = this.activeFields;
      } else if (Array.isArray(this.fields)) {
        fields = this.fields;
      } else if (Array.isArray(this.activeUserFields)) {
        fields = this.activeUserFields;
      } else if (Array.isArray(this.activeGroupFields)) {
        fields = this.activeGroupFields;
      }

      if (!this.printing) {
        return fields;
      }

      return fields.filter((field) => field.key !== "details");
    },

    printRootClass() {
      if (!this.printing) {
        return null;
      }

      const fieldCount = this.printFields.length;

      return {
        "print-layout-landscape": this.printOrientation === "landscape",
        "print-layout-portrait": this.printOrientation === "portrait",
        "print-cols-dense": fieldCount > 8,
        "print-cols-extra-dense": fieldCount > 12,
      };
    },

    printFileName() {
      const reportTitle = this.printTitle || this.reportTitle || "Relatório";
      const company = this.printCompany;
      const normalizedTitle = this.sanitizePrintFilePart(reportTitle);
      const normalizedCompany = this.sanitizePrintFilePart(company);
      const isAllCompanies =
        !company ||
        company === "Todas Empresas" ||
        company === "Empresa não identificada";
      const hasCompanyInTitle =
        normalizedCompany &&
        normalizedTitle.toLowerCase().includes(normalizedCompany.toLowerCase());
      const reportPart =
        !isAllCompanies && normalizedCompany && !hasCompanyInTitle
          ? `${normalizedTitle}_${normalizedCompany}`
          : normalizedTitle;
      const datePart = moment(this.printGeneratedAt || new Date()).format(
        "YYYY-MM-DD"
      );

      return `${reportPart || "Relatório"}_${datePart}`;
    },

    printPortalBucket() {
      if (this.portalBucket) {
        return this.portalBucket;
      }

      return this.$store &&
        this.$store.getters &&
        this.$store.getters.portalBucket
        ? this.$store.getters.portalBucket
        : null;
    },
  },

  methods: {
    sanitizePrintFilePart(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },

    formatPrintDate(value) {
      if (!value) {
        return "—";
      }

      const date = moment(value, "YYYY-MM-DD", true);
      return date.isValid()
        ? date.format("DD/MM/YYYY")
        : moment(value).format("DD/MM/YYYY");
    },

    formatPrintDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },

    async loadCompanyPrintLogo(companyId) {
      this.companyPrintLogo = null;

      if (!companyId || !this.printPortalBucket) {
        return;
      }

      try {
        const response = await getCompanyInfo(companyId);
        const data = response.data || {};

        if (
          data.portalLogo &&
          data.portalLogo.portalId &&
          data.portalLogo.filename
        ) {
          this.companyPrintLogo = `https://${this.printPortalBucket}/${data.portalLogo.portalId}/${data.portalLogo.filename}`;
          return;
        }

        if (data.whitelabel && data.whitelabel.logo) {
          const logo = data.whitelabel.logo;
          this.companyPrintLogo = /^https?:\/\//i.test(logo)
            ? logo
            : `https://${this.printPortalBucket}/${logo}`;
        }
      } catch (error) {
        console.error(
          "Erro ao carregar logo da empresa para impressão:",
          error
        );
      }
    },

    async printReport({ onComplete } = {}) {
      this.printing = true;
      this.printGeneratedAt = new Date();
      this.printOrientation =
        this.printFields.length > 4 ? "landscape" : "portrait";

      await this.$nextTick();

      const assetUrl = (asset) => new URL(asset, window.location.href).href;
      const previousDocumentTitle = document.title;
      const orientationStyles =
        this.printOrientation === "landscape"
          ? assetUrl("printing-landscape.css")
          : assetUrl("printing-portrait.css");

      const cleanup = () => {
        this.printing = false;
        this.printGeneratedAt = null;
        this.printOrientation = "portrait";
        document.title = previousDocumentTitle;
        if (onComplete) {
          onComplete();
        }
      };

      document.title = this.printFileName;

      try {
        this.$htmlToPaper(
          "report",
          {
            styles: [
              "https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css",
              assetUrl("printing.css"),
              orientationStyles,
            ],
          },
          cleanup
        );
      } catch (error) {
        cleanup();
        throw error;
      }
    },

    async print() {
      return this.printReport();
    },
  },
};
